import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Users, X, UserPlus, Plus, Trash2 } from "lucide-react";
import ListTemplate from "../_templates/ListTemplate";
import BulkDeleteAction from "../../../components/common/BulkDeleteAction";
import { listTeams, deleteTeam } from "../../../api/coredata.api";
import useNationalities from "../../../hooks/useNationalities";
import { playerDisplayName } from "../../../hooks/useNameMode";
import useDialogDismiss from "../../../hooks/useDialogDismiss";
import api from "../../../api/axios";
import useAuthStore from "../../../store/auth.store";

const TYPE_TONE = {
  INTERNATIONAL: "bg-button-primary/20 text-button-primary border-button-primary/40",
  DOMESTIC:      "bg-blue-500/15 text-blue-300 border-blue-400/30",
  LEAGUE:        "bg-amber-500/15 text-amber-300 border-amber-400/30",
};
const Pill = ({ value, tone }) =>
  value ? (
    <span className={`inline-block text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${tone || "bg-bg-secondary text-text-secondary border-border-default"}`}>
      {value}
    </span>
  ) : null;

export default function TeamList() {
  const { nameOf } = useNationalities();
  const isSuperuser = useAuthStore((s) => !!s.user?.is_superuser);
  // Open-squad target — set when the operator clicks "Squad" on a team row.
  const [squadFor, setSquadFor] = useState(null); // {teamId, teamName}

  return (
    <>
      <Link
        to="/marketplace"
        className="mb-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-400/40 text-purple-200 hover:bg-purple-500/15 transition"
      >
        <ShoppingBag className="w-3.5 h-3.5" />
        Need ready-made tournament teams + squads? Browse marketplace packs →
      </Link>
      <ListTemplate
        title="Teams"
        fetchFn={listTeams}
        deleteFn={deleteTeam}
        getAddPath={() => "/cricket/teams/add"}
        getEditPath={(id) => `/cricket/teams/${id}/edit`}
        bulkActions={isSuperuser ? (ids, reset, reload) => (
          <BulkDeleteAction ids={ids} deleteFn={deleteTeam} label="teams" reset={reset} reload={reload} />
        ) : undefined}
        columns={[
          { key: "name",          label: "Team",     render: (r) => <span className="font-medium">{r.name}</span> },
          { key: "short_name",    label: "Short",    render: (r) => <span className="font-mono text-xs text-text-secondary">{r.short_name}</span> },
          { key: "team_type",     label: "Type",     render: (r) => <Pill value={r.team_type} tone={TYPE_TONE[r.team_type]} /> },
          { key: "team_category", label: "Category", render: (r) => <Pill value={r.team_category} /> },
          { key: "nationality",   label: "Country",  render: (r) => (
            <div className="flex items-center gap-2">
              <span>{nameOf(r.nationality)}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSquadFor({ teamId: r.id, teamName: r.name });
                }}
                title="View squad — players linked to this team"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20"
              >
                <Users className="w-3 h-3" />
                Squad
              </button>
            </div>
          )},
        ]}
      />
      <TeamSquadModal
        open={!!squadFor}
        teamId={squadFor?.teamId}
        teamName={squadFor?.teamName}
        onClose={() => setSquadFor(null)}
      />
    </>
  );
}

function TeamSquadModal({ open, teamId, teamName, onClose }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  // Add-player UI state (2026-06-04 — mid-season replacements).
  const [addQuery, setAddQuery] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const debounceRef = useRef(null);
  const panelRef = useRef(null);
  useDialogDismiss({ open, onClose, ref: panelRef });

  const reloadSquad = () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    api.get(`/api/coredata/teams/${teamId}/squad/`)
      .then((res) => {
        const list = res.data?.players || res.data || [];
        setPlayers(Array.isArray(list) ? list : []);
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message || "Failed to load squad"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !teamId) return;
    reloadSquad();
    setAddQuery("");
    setAddResults([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamId]);

  // Debounced player search — hits the players list endpoint with the
  // operator's query. Excludes players already in the squad so the
  // operator doesn't see duplicates in the dropdown.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = addQuery.trim();
    if (!q) { setAddResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setAddLoading(true);
      try {
        const res = await api.get("/api/coredata/players/", {
          params: { search: q, page_size: 20 },
        });
        const all = res.data?.results || res.data || [];
        const inSquad = new Set(players.map((p) => String(p.id)));
        setAddResults((Array.isArray(all) ? all : []).filter((p) => !inSquad.has(String(p.id))));
      } catch {
        setAddResults([]);
      } finally {
        setAddLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [addQuery, open, players]);

  const addPlayer = async (player) => {
    setActionBusy(player.id);
    try {
      await api.post(`/api/coredata/teams/${teamId}/squad/`, { player_id: player.id });
      setAddQuery("");
      setAddResults([]);
      reloadSquad();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to add player");
    } finally {
      setActionBusy(null);
    }
  };

  const removePlayer = async (player) => {
    if (!window.confirm(`Remove ${playerDisplayName(player)} from the squad?`)) return;
    setActionBusy(player.id);
    try {
      await api.delete(`/api/coredata/teams/${teamId}/squad/`, { data: { player_id: player.id } });
      reloadSquad();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to remove player");
    } finally {
      setActionBusy(null);
    }
  };

  if (!open) return null;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? players.filter((p) =>
        `${p.first_name || ""} ${p.last_name || ""} ${p.nick_name || ""} ${p.role || ""}`
          .toLowerCase().includes(q),
      )
    : players;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={teamName ? `${teamName} squad` : "Team squad"}
        className="bg-bg-primary rounded-2xl max-w-2xl w-full max-h-[85vh] border border-bg-secondary shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-default">
          <div className="flex items-center gap-2 min-w-0">
            <Users className="w-5 h-5 text-blue-400" />
            <h3 className="text-base font-semibold text-text-primary truncate">
              {teamName ? `${teamName} — Squad` : "Team Squad"}
            </h3>
            {!loading && (
              <span className="text-[11px] text-text-secondary">{filtered.length} player{filtered.length === 1 ? "" : "s"}</span>
            )}
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Add player — search existing or jump to create a new one.
            Mid-season replacements (injury, impact sub) no longer
            require leaving this modal. */}
        <div className="px-5 pt-3 pb-2 border-b border-border-default bg-bg-secondary/30">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="w-4 h-4 text-emerald-300" />
            <span className="text-[11px] uppercase tracking-wider text-emerald-300">Add player to squad</span>
            <Link
              to="/cricket/players/add"
              className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
              title="Open the player-create page in a new tab, then come back and search"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Plus className="w-3 h-3" />
              Create new player
            </Link>
          </div>
          <input
            value={addQuery}
            onChange={(e) => setAddQuery(e.target.value)}
            placeholder="Search by name to add…"
            className="w-full bg-bg-primary border border-border-default rounded px-3 py-1.5 text-sm"
          />
          {addQuery && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded border border-border-default bg-bg-primary">
              {addLoading && (
                <div className="px-3 py-2 text-xs text-text-secondary">Searching…</div>
              )}
              {!addLoading && addResults.length === 0 && (
                <div className="px-3 py-2 text-xs text-text-secondary">
                  No matches. Use <span className="text-emerald-300">Create new player</span> above for a brand-new player.
                </div>
              )}
              {!addLoading && addResults.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => addPlayer(p)}
                  disabled={actionBusy === p.id}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-bg-secondary border-b border-border-default last:border-b-0 flex items-center gap-2 disabled:opacity-60"
                >
                  <Plus className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="text-text-primary truncate">{playerDisplayName(p)}</span>
                  {p.role && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-text-secondary">{p.role}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 pt-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search current squad…"
            className="w-full bg-bg-secondary border border-border-default rounded px-3 py-1.5 text-sm"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="text-text-secondary text-sm">Loading squad…</div>}
          {!loading && error && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded p-3 text-sm text-rose-300">{error}</div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="text-text-secondary text-sm py-6 text-center">
              {players.length === 0
                ? "No players in this squad yet — use the Add player search above to add one."
                : "No players match that search."}
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <ul className="divide-y divide-border-default">
              {filtered.map((p) => (
                <li key={p.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-text-primary truncate">
                      {/* playerDisplayName embeds the jersey in parens,
                          e.g. "Virat (18)". A separate badge would dup. */}
                      {playerDisplayName(p)}
                    </div>
                    <div className="text-[11px] text-text-secondary flex flex-wrap gap-x-2 gap-y-0.5">
                      {p.role && <span>{p.role}</span>}
                      {p.batting_hand && <span>Bat: {p.batting_hand}</span>}
                      {p.bowling_hand && <span>Bowl: {p.bowling_hand}</span>}
                      {p.bowling_type && <span>{p.bowling_type}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={`/cricket/players/${p.id}/edit`}
                      className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border border-border-default text-text-secondary hover:text-text-primary"
                    >
                      Edit
                    </a>
                    <button
                      type="button"
                      onClick={() => removePlayer(p)}
                      disabled={actionBusy === p.id}
                      title="Remove from squad"
                      className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 disabled:opacity-60 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
